/**
 * Context window size based on MS documentation
 * {@link https://learn.microsoft.com/en-us/azure/ai-foundry/openai/concepts/models?tabs=global-standard%2Cstandard-chat-completions}
 */
const aiConfig = {
  OPEN_AI: {
    MODELS: {
      CHAT: [
        {
          name: "GPT_3",
          displayName: "GPT 3",
          isReasoning: false,
          description:
            "Reliable general-purpose model for everyday questions and tasks.",
          deploymentName: null,
          color: "var(--brand-ter-gray-500)",
          legacy: true,
        },
        {
          name: "GPT_4",
          displayName: "GPT 4",
          isReasoning: false,
          description:
            "Advanced general-purpose model with improved understanding.",
          deploymentName: null,
          color: "var(--brand-tl-red-500)",
          legacy: true,
        },
        {
          name: "GPT_o3_MINI",
          displayName: "GPT o3 mini",
          isReasoning: true,
          description:
            "Small, fast reasoning model for straightforward problem solving.",
          deploymentName: "o3-mini",
          contextSize: 200_000,
          color: "var(--brand-ter-blue-500)",
          legacy: true,
        },
        {
          name: "GPT_4o_MINI",
          displayName: "GPT 4o mini",
          isReasoning: false,
          description: "Lightweight model for quick and simple general tasks.",
          deploymentName: null,
          color: "var(--brand-ter-green-500)",
          legacy: true,
        },
        {
          name: "GPT_4o",
          displayName: "GPT 4o",
          isReasoning: false,
          description:
            "Fast, efficient model for a wide range of general tasks.",
          deploymentName: "gpt-4o-2024-11-20",
          contextSize: 128_000,
          color: "var(--brand-tl-green-500)",
          legacy: true,
        },
        {
          name: "GPT_o4_MINI",
          displayName: "GPT o4 mini",
          isReasoning: true,
          description:
            "Compact reasoning model for basic analytical and logic tasks.",
          deploymentName: "o4-mini",
          contextSize: 200_000,
          color: "var(--brand-ter-bluegray-500)",
          legacy: true,
        },
        {
          name: "GPT_4.1",
          displayName: "GPT 4.1",
          isReasoning: false,
          description:
            "Latest general model with enhanced accuracy and knowledge.",
          deploymentName: "gpt-4.1",
          contextSize: 1_047_576,
          color: "var(--brand-ter-blue-400)",
          legacy: true,
        },
        {
          name: "GPT_o3",
          displayName: "GPT o3",
          isReasoning: true,
          description:
            "Advanced reasoning model for complex analysis and logic.",
          deploymentName: "o3",
          contextSize: 200_000,
          color: "var(--brand-ter-green-400)",
          legacy: true,
        },
        {
          name: "GPT_4o_REALTIME_PREVIEW",
          displayName: "GPT 4o RT Preview",
          color: "var(--brand-tl-yellow-500)",
          isReasoning: false,
          description:
            "Experimental model with real-time capabilities, suitable for dynamic interactions.",
          deploymentName: "gpt-realtime-2025-08-28",
          contextSize: 128_000,
          legacy: true,
        },
        {
          name: "GPT_model_router",
          displayName: "Auto",
          isReasoning: true,
          description:
            "A model that intelligently selects from a set of underlying chat models to respond to a given prompt.",
          deploymentName: "model-router-2025-08-07",
          contextSize: 200_000,
          color: "var(--brand-ter-bluegray-400)",
          legacy: false,
        },
        {
          name: "GPT_5",
          displayName: "GPT 5 (Reasoning)",
          isReasoning: true,
          description:
            "GPT-5 is flagship model for coding, reasoning, and agentic tasks across domains",
          deploymentName: "gpt-5-2025-08-07",
          contextSize: 400_000,
          color: "var(--brand-ter-blue-300)",
          legacy: true,
        },
        {
          name: "GPT_5_MINI",
          displayName: "GPT 5 Mini (Reasoning)",
          isReasoning: true,
          description:
            "GPT-5 mini is a faster, more cost-efficient version of GPT-5. It's great for well-defined tasks and precise prompts.",
          deploymentName: "gpt-5-mini-2025-08-07",
          contextSize: 400_000,
          color: "var(--brand-ter-green-300)",
          legacy: true,
        },
        {
          name: "GPT_5_NANO",
          displayName: "GPT 5 Nano (Reasoning)",
          isReasoning: true,
          description:
            "GPT-5 Nano is fastest, cheapest version of GPT-5. It's great for summarization and classification tasks.",
          deploymentName: "gpt-5-nano-2025-08-07",
          contextSize: 400_000,
          color: "var(--brand-ter-bluegray-300)",
          legacy: true,
        },
        {
          name: "GPT_5_CHAT",
          displayName: "GPT 5 (Chat)",
          isReasoning: false,
          description:
            "GPT-5 Chat points to the GPT-5 snapshot currently used in ChatGPT, it's recommended for improved chat use cases.",
          deploymentName: "gpt-5-chat-2025-08-07",
          contextSize: 128_000,
          color: "var(--brand-ter-gray-400)",
          legacy: true,
        },
        {
          name: "GPT_5.2",
          displayName: "GPT 5.2 (Reasoning)",
          isReasoning: true,
          description:
            "GPT-5.2 is flagship model for coding and agentic tasks across industries. ",
          deploymentName: "gpt-5.2-chat-2025-12-11",
          contextSize: 400_000,
          color: "var(--brand-ter-blue-200)",
          legacy: true,
        },
        {
          name: "GPT_5.2_CHAT",
          displayName: "GPT 5.2 (Chat)",
          isReasoning: false,
          description:
            "GPT-5.2 Chat points to the GPT-5.2 snapshot currently used in ChatGPT. It offers smarter and more natural conversations for most needs.",
          deploymentName: "gpt-5.2-chat-2025-12-11",
          contextSize: 128_000,
          color: "var(--brand-ter-green-200)",
          legacy: true,
        },
        {
          name: "GPT_5.4",
          displayName: "GPT 5.4",
          isReasoning: true,
          description:
            "Most advanced model for coding, deep reasoning, and complex multi-step tasks.",
          deploymentName: "gpt-5.4-2026-03-05",
          contextSize: 128_000,
          color: "var(--brand-ter-blue-100)",
          legacy: true,
        },
        {
          name: "GPT_5.5",
          displayName: "GPT 5.5",
          isReasoning: true,
          description:
            "Latest and most powerful model with superior reasoning, coding, and agentic capabilities.",
          deploymentName: "gpt-5.5-2026-04-24",
          contextSize: 128_000,
          color: "var(--brand-ter-blue-100)",
          legacy: false,
        },
      ],
      IMAGE: [
        {
          name: "gpt-image-1",
          deploymentName: "gpt-image-1-2025-04-15",
        },
        {
          name: "gpt-image-1.5",
          deploymentName: "gpt-image-1.5-2025-12-16",
        },
        {
          name: "gpt-image-2",
          deploymentName: "gpt-image-2",
        },
      ],
    },
  },
  AWS_BEDROCK: {
    MODELS: {
      CHAT: [
        {
          name: "BEDROCK_CLAUDE_4_SONNET",
          displayName: "Claude Sonnet 4",
          isReasoning: true,
          description:
            "Advanced reasoning model for complex analysis and logic.",
          deploymentName: "eu.anthropic.claude-sonnet-4-20250514-v1:0",
          contextSize: 200_000,
          color: "var(--brand-ter-bluegray-200)",
          legacy: true,
        },
        {
          name: "BEDROCK_CLAUDE_45_OPUS",
          displayName: "Claude Opus 4.5",
          isReasoning: true,
          description:
            "Advanced reasoning model for complex analysis and logic.",
          deploymentName: "eu.anthropic.claude-opus-4-5-20251101-v1:0",
          contextSize: 200_000,
          color: "var(--brand-ter-gray-300)",
          legacy: true,
        },
        {
          name: "BEDROCK_CLAUDE_46_OPUS",
          displayName: "Claude Opus 4.6",
          isReasoning: true,
          description:
            "Latest reasoning model for complex analysis and logic, with improved performance.",
          deploymentName: "eu.anthropic.claude-opus-4-6-v1",
          contextSize: 200_000,
          color: "var(--brand-ter-gray-300)",
          legacy: false,
        },
      ],
    },
  },
} as const;

type OpenAIModels = (typeof aiConfig.OPEN_AI.MODELS.CHAT)[number]["name"];
type BedrockModels = (typeof aiConfig.AWS_BEDROCK.MODELS.CHAT)[number]["name"];

export type OpenAiImageModels =
  (typeof aiConfig.OPEN_AI.MODELS.IMAGE)[number]["name"];

const openAIModels = [
  ...aiConfig.OPEN_AI.MODELS.CHAT.map((model) => model.name),
] as unknown as Readonly<[keyof Record<OpenAIModels, unknown>]>;

const bedrockModels = [
  ...aiConfig.AWS_BEDROCK.MODELS.CHAT.map((model) => model.name),
] as unknown as Readonly<[keyof Record<BedrockModels, unknown>]>;

export const AiProfileModelEnumValues = [
  ...openAIModels,
  ...bedrockModels,
] as unknown as Readonly<[keyof Record<OpenAIModels | BedrockModels, unknown>]>;

export type AiProfileModelEnumType = (typeof AiProfileModelEnumValues)[number];

type ModelMetadataDetails = {
  isReasoning: boolean;
  description: string;
  color: string;
  displayName: string;
  legacy: boolean;
};

type ModelMetadata = Record<AiProfileModelEnumType, ModelMetadataDetails>;

const modelMetadata: ModelMetadata = openAIModels.reduce((acc, model) => {
  const modelInfo = aiConfig.OPEN_AI.MODELS.CHAT.find((m) => m.name === model);

  if (!modelInfo) {
    throw new Error(`Model info not found for model: ${model}`);
  }

  acc[model] = {
    isReasoning: modelInfo.isReasoning,
    description: modelInfo.description,
    color: modelInfo.color,
    displayName: modelInfo.displayName,
    legacy: modelInfo.legacy ?? false,
  };
  return acc;
}, {} as ModelMetadata);

bedrockModels.reduce((acc, model) => {
  const modelInfo = aiConfig.AWS_BEDROCK.MODELS.CHAT.find(
    (m) => m.name === model,
  );

  if (!modelInfo) {
    throw new Error(`Model info not found for model: ${model}`);
  }

  acc[model] = {
    isReasoning: modelInfo.isReasoning,
    description: modelInfo.description,
    color: modelInfo.color,
    displayName: modelInfo.displayName,
    legacy: modelInfo.legacy ?? false,
  };
  return acc;
}, modelMetadata);

export const getModelMetadata = (
  model: AiProfileModelEnumType,
): {
  name: AiProfileModelEnumType;
} & ModelMetadataDetails => {
  const modelInfo = modelMetadata[model];

  if (!modelInfo) {
    throw new Error(`Model info not found for model: ${model}`);
  }

  return {
    name: model,
    isReasoning: modelInfo.isReasoning,
    description: modelInfo.description,
    color: modelInfo.color,
    displayName: modelInfo.displayName,
    legacy: modelInfo.legacy,
  };
};

export type AvailableModel = Extract<
  AiProfileModelEnumType,
  | "GPT_4o"
  | "GPT_4.1"
  | "GPT_o4_MINI"
  | "GPT_o3_MINI"
  | "GPT_o3"
  | "GPT_model_router"
  | "BEDROCK_CLAUDE_4_SONNET"
  | "BEDROCK_CLAUDE_45_OPUS"
  | "GPT_4o_REALTIME_PREVIEW"
  | "GPT_5"
  | "GPT_5_MINI"
  | "GPT_5_NANO"
  | "GPT_5_CHAT"
  | "GPT_5.2"
  | "GPT_5.2_CHAT"
  | "GPT_5.4"
  | "BEDROCK_CLAUDE_46_OPUS"
  | "GPT_5.5"
>;

export const AvailableModels: AvailableModel[] = [
  "GPT_4o",
  "GPT_4.1",
  "GPT_o4_MINI",
  "GPT_o3_MINI",
  "GPT_o3",
  "GPT_model_router",
  "BEDROCK_CLAUDE_4_SONNET",
  "BEDROCK_CLAUDE_45_OPUS",
  "GPT_5",
  "GPT_5_MINI",
  "GPT_5_NANO",
  "GPT_5_CHAT",
  "GPT_5.2",
  "GPT_5.2_CHAT",
  "GPT_5.4",
  "BEDROCK_CLAUDE_46_OPUS",
  "GPT_5.5",
];

// Models where reasoning can be toggled on/off in profile settings.
export const OptionalReasoningModels: ReadonlyArray<AvailableModel> = [
  "GPT_5.4",
  "GPT_5.5",
];

export const isOptionalReasoningModel = (
  modelName: AiProfileModelEnumType,
): boolean => {
  const availableModel = mapAIModelToChatAvailableModel(modelName);

  return OptionalReasoningModels.includes(availableModel);
};

export const DefaultAiProfileModelEnumType: AvailableModel = "GPT_4.1";

// Default model used for new Chat AI profile creation flows.
export const DefaultChatAiProfileModelEnumType: AvailableModel = "GPT_5.5";

// Deployment used for RAG pipeline (graph extraction, community detection).
export const RagDeploymentName = "gpt-4.1-mini-2025-04-14";

export type ModelRouterModelNames = Extract<
  AiProfileModelEnumType,
  "GPT_model_router"
>;

export type ModelRouterDeployment = Extract<
  AvailableModelDeploymentsEnumType,
  "model-router-2025-08-07"
>;

/**
 * Base object describing Model router behavior
 */
const modelRouterDeployments = {
  modelRouter: "GPT_model_router",
  modelRouterDeployment: "model-router-2025-08-07",
  underlyingModels: {
    "gpt-4.1-nano-2025-04-14": "GPT_4.1_NANO",
    "gpt-4.1-mini-2025-04-14": "GPT_4.1_MINI",
    "gpt-4.1-2025-04-14": "GPT_4.1",
    "o4-mini-2025-04-16": "GPT_o4_MINI",
    "gpt-5-2025-08-07": "GPT_5",
    "gpt-5-mini-2025-08-07": "GPT_5_MINI",
    "gpt-5-nano-2025-08-07": "GPT_5_NANO",
    "gpt-5-chat-2025-08-07": "GPT_5_CHAT",
    "gpt-5-chat-latest": "GPT_5_CHAT",
    "gpt-5.2-2025-12-11": "GPT_5.2",
    "gpt-5.2-chat-2025-12-11": "GPT_5.2_CHAT",
    "gpt-5.4-2026-03-05": "GPT_5.4",
    "gpt-5.5-2026-04-24": "GPT_5.5",
  },
} as const;

export const ModelRouterUnderlyingModels =
  modelRouterDeployments.underlyingModels;

type ModelDeployments = Readonly<
  [keyof typeof modelRouterDeployments.underlyingModels]
>;

/**
 * Used for DB enum declaration
 */
export const ModelRouterUnderlyingModelDeployments: ModelDeployments =
  Object.keys(
    modelRouterDeployments.underlyingModels,
  ) as unknown as ModelDeployments;

export type ModelRouterUnderlyingModelDeploymentsType =
  (typeof ModelRouterUnderlyingModelDeployments)[number];

type ModelRouterDeploymentsType = typeof modelRouterDeployments;

/**
 * Used for extraction model name from `modelRouterDeployments.underlyingModels`.
 * It uses type pattern matching relying on structure of `modelRouterDeployments` and current Model Router.
 * In case of successful matched Model Router type it will extract model names based on Base object.
 * deployment name. Type output may be presented as `"GPT_4.1_NANO" | "GPT_4.1_MINI" | "GPT_4.1" | "GPT_o4_MINI"`
 */
export type ModelRouterUnderlyingModelNameType<
  TModelRouterDeployment = ModelRouterDeployment,
  TModelRouterDeploymentsType = ModelRouterDeploymentsType,
> = TModelRouterDeploymentsType extends infer TargetModelRouterDeployments extends
  {
    modelRouterDeployment: TModelRouterDeployment;
    underlyingModels: {
      [ObjectKey in keyof TModelRouterDeploymentsType]: TModelRouterDeploymentsType[ObjectKey];
    }[keyof TModelRouterDeploymentsType];
  }
  ? TargetModelRouterDeployments["underlyingModels"] extends infer DeploymentNameKey
    ? DeploymentNameKey[keyof DeploymentNameKey]
    : never
  : never;

/**
 * Used for extraction model deployment name from `modelRouterDeployments.underlyingModels`.
 * It uses type pattern matching relying on structure of `modelRouterDeployments` and current Model Router.
 * In case of successful matched Model Router type it will extract model deployment names based on Base object
 * deployment name. Type output may be presented as `"gpt-4.1-nano-2025-04-14" | "gpt-4.1-mini-2025-04-14" | "gpt-4.1-2025-04-14" | "o4-mini-2025-04-16"`
 */
export type ModelRouterUnderlyingModelDeploymentType<
  TModelRouterDeployment = ModelRouterDeployment,
  TModelRouterDeploymentsType = ModelRouterDeploymentsType,
> = TModelRouterDeploymentsType extends infer TargetModelRouterDeployments extends
  {
    modelRouterDeployment: TModelRouterDeployment;
    underlyingModels: {
      [ObjectKey in keyof TModelRouterDeploymentsType]: TModelRouterDeploymentsType[ObjectKey];
    }[keyof TModelRouterDeploymentsType];
  }
  ? TargetModelRouterDeployments["underlyingModels"] extends infer DeploymentNameKey
    ? keyof DeploymentNameKey
    : never
  : never;

/**
 * Constructing type based on declared Model Router deployment type
 * Used for outside pattrn matching,
 * e.g. for AiModel badge name component
 */
type ModelRule<TModelRouterDeployment extends ModelRouterDeployment | null> =
  TModelRouterDeployment extends infer CurrentModelRouterDeployment extends
    ModelRouterDeployment
    ? {
        modelName: CurrentModelRouterDeployment;
        usedModel: ModelRouterUnderlyingModelDeploymentType<CurrentModelRouterDeployment>;
      }
    : never;
/**
 * Matches type based on provided generic context,
 * e.g. TModelRouterDeployment == ModelRouterDeployment -> ModelRule<ModelRouterDeployment>
 * TModelRouterDeployment == null -> { name: AiProfileModelEnumType}
 */
export type AiModel<
  TModelRouterDeployment extends ModelRouterDeployment | null = null,
> =
  | {
      name: AiProfileModelEnumType;
    }
  | ModelRule<TModelRouterDeployment>;

export type ModelName =
  | AiProfileModelEnumType
  | ModelRouterUnderlyingModelNameType<ModelRouterDeployment>;

export const formatModelName = (
  modelName: ModelName,
  options?: {
    reasoningEnabled?: boolean;
  },
) => {
  if (isChatAiProfileDisplayModel(modelName)) {
    const availableModel = mapAIModelToChatAvailableModel(modelName);

    const metadata = getModelMetadata(availableModel);

    if (isOptionalReasoningModel(availableModel)) {
      return options?.reasoningEnabled
        ? `${metadata.displayName} (Reasoning)`
        : metadata.displayName;
    }

    return metadata.displayName;
  } else {
    switch (modelName) {
      //#region Model Router | These models aren't available in our AI Profiles
      case "GPT_4.1_MINI":
        return "GPT 4.1 mini";
      case "GPT_4.1_NANO":
        return "GPT 4.1 nano";
      //#endregion Model Router
    }
  }
};

export type AvailableModelDeploymentsEnumType = Exclude<
  | (typeof aiConfig.OPEN_AI.MODELS.CHAT)[number]["deploymentName"]
  | (typeof aiConfig.AWS_BEDROCK.MODELS.CHAT)[number]["deploymentName"],
  null
>;

export type AvailableImageModelDeploymentsEnumType =
  (typeof aiConfig.OPEN_AI.MODELS.IMAGE)[number]["deploymentName"];

const chatModelDeploymentConfig = aiConfig.OPEN_AI.MODELS.CHAT.reduce(
  (acc, model) => {
    if (model.deploymentName) {
      acc[model.name] = [model.deploymentName, model.contextSize];
    }
    return acc;
  },
  {} as Record<AvailableModel, [AvailableModelDeploymentsEnumType, number]>,
);

aiConfig.AWS_BEDROCK.MODELS.CHAT.reduce((acc, model) => {
  if (model.deploymentName) {
    acc[model.name] = [model.deploymentName, model.contextSize];
  }
  return acc;
}, chatModelDeploymentConfig);

const imageModelDeploymentConfig = aiConfig.OPEN_AI.MODELS.IMAGE.reduce(
  (acc, model) => {
    if (model.deploymentName) {
      acc[model.name] = [model.deploymentName];
    }
    return acc;
  },
  {} as Record<OpenAiImageModels, [AvailableImageModelDeploymentsEnumType]>,
);

export const mapAiModelToDeploymentName = (
  modelName: AiProfileModelEnumType,
): AvailableModelDeploymentsEnumType => {
  const actualModel = mapAIModelToChatAvailableModel(modelName);

  const [deploymentName] = chatModelDeploymentConfig[actualModel];

  return deploymentName;
};

export const mapImageModelToDeploymentName = (
  modelName: OpenAiImageModels,
): AvailableImageModelDeploymentsEnumType => {
  const [deploymentName] = imageModelDeploymentConfig[modelName];

  return deploymentName;
};

const isChatAiProfileDisplayModel = (
  model:
    | AiProfileModelEnumType
    | ModelRouterUnderlyingModelNameType<ModelRouterDeployment>,
): model is AiProfileModelEnumType => {
  return AiProfileModelEnumValues.includes(model as AiProfileModelEnumType);
};

/**
 * Used when we're migrating from old model versions to new ones.
 * Generally used on runtime to ensure we only return models that
 * are available for our AI Profiles and populated in infrastructure.
 */
export const mapAIModelToChatAvailableModel = (
  modelName: AiProfileModelEnumType,
): AvailableModel => {
  switch (modelName) {
    case "GPT_3":
    case "GPT_4":
    case "GPT_4o_MINI":
      return "GPT_4o";
    default:
      return modelName;
  }
};

/**
 * Determine context size based on deployment name.
 * @param model
 * @returns context window size
 */
export const getModelContextSize = (model: AiProfileModelEnumType) => {
  const actualModel = mapAIModelToChatAvailableModel(model);
  const [_, contextSize] = chatModelDeploymentConfig[actualModel];

  return contextSize;
};
